import type { InputProps } from "@mantine/core";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import {
  type Control,
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  type ValidationRule
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormulaData } from "../FormFields/ExpressionEditor/ExpressionEditor.types";
import { type FormFieldTextInputProps } from "../MantineFormFields/fields/FormFieldTextInput";
import type {
  CommonFormFieldProps,
  FormFieldProps
} from "../MantineFormFields/FormField";
import { FormField } from "../MantineFormFields/FormField";
import { validateBoolean } from "../utils/validateBoolean";
import { validateExpression } from "../utils/validateExpression";

export type FormInputData<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = {
  name: TName;
  /** alias for name. can be used for buildFieldNameToLabelMap */
  alias?: string;
  /** e.g. for Tab-specific error tracking */
  group?: string;
  /** dont render if value is null */
  hiddenIfNull?: boolean;
  /** hide the field error of FormField, but still validate e.g. if you want to show the error somewhere else */
  hideFieldError?: boolean;
} & (
  | FormFieldProps
  | (CommonFormFieldProps &
      InputProps & {
        label?: string;
        type: "custom";
      })
);

export type FormFieldData<T extends FieldValues> = {
  [K in FieldPath<T>]?: FormInputData<T, K>;
};

interface FormFieldControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>
  extends
    Pick<ControllerProps<TFieldValues, TName>, "rules">,
    Pick<FormFieldProps, "error"> {
  control: Control<TFieldValues>;
  data: FormInputData<TFieldValues, TName>;
}

function getValidateFunctionFromFieldType({
  data,
  rulesRequired,
  t
}: {
  data: FormInputData<FieldValues, FieldPath<FieldValues>>;
  rulesRequired?: string | ValidationRule<boolean>;
  t: TFunction;
}) {
  if (data.type === "boolean" && (rulesRequired || data.required)) {
    return (value: unknown) => validateBoolean(value, t("errors.Required"));
  }

  if (data.type === "expression") {
    return (value: FormulaData) =>
      validateExpression({
        value,
        templates: data.formulaTemplates,
        required: rulesRequired || data.required ? true : false,
        t
      });
  }

  return undefined;
}

function FormFieldController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  control,
  data,
  error,
  rules
}: FormFieldControllerProps<TFieldValues, TName>) {
  const { t } = useTranslation();
  const completeRules = useMemo(
    () =>
      data
        ? {
            ...rules,
            required:
              rules?.validate ||
              data?.type === "boolean" ||
              data?.type === "expression"
                ? undefined
                : rules?.required || data?.required,
            min:
              data && data.type === "number" && typeof data.min === "number"
                ? {
                    value: data.min,
                    message: t("errors.Number.MinNumber", { min: data.min })
                  }
                : undefined,
            max:
              data && data.type === "number" && typeof data.max === "number"
                ? {
                    value: data.max,
                    message: t("errors.Number.MaxNumber", { max: data.max })
                  }
                : undefined,
            validate: rules?.validate
              ? rules.validate
              : getValidateFunctionFromFieldType({
                  data,
                  rulesRequired: rules?.required,
                  t
                })
          }
        : undefined,
    [rules, data, t]
  );

  if (!data) {
    console.error(
      "No FormInputData was provided to FormFieldController. Check that data exists for this field."
    );
    return null;
  }

  const { hiddenIfNull, hideFieldError, ...fieldSpecificData } = data;

  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={data.name}
      render={({ field, fieldState: { error: fieldError } }) => {
        if (hiddenIfNull && field.value === null) {
          return <></>;
        }

        return (
          <FormField
            {...(fieldSpecificData as FormFieldTextInputProps)}
            {...field}
            disabled={
              "disabled" in data || "disabled" in field
                ? data.disabled || field.disabled
                : false
            }
            error={fieldError || error}
            hideFieldError={hideFieldError}
          />
        );
      }}
      rules={completeRules}
    />
  );
}

export { FormFieldController, FormFieldControllerProps };
